import { datadogLogs } from '@datadog/browser-logs';
import type { SerializedError } from '@reduxjs/toolkit';
import { notifyDatadogRumAction } from 'cfa-react-logging';

import ooeConstants from '../constants';

export const DATADOG_EVENT_NAMES = {
  DELIVERY_OR_PICKUP_SELECTED: 'DeliveryOrPickupSelected',
  GUEST_ADDED: 'GuestAdded',
  PICKUP_OR_DELIVERY_TIME_SET: 'PickupOrDeliveryTimeSet',
  DELIVERY_ADDRESS_ADDED: 'DeliveryAddressAdded',
  DELIVERY_ADDRESS_VALIDATED: 'DeliveryAddressValidated',
  MENU_ITEM_ADDED: 'MenuItemAdded',
  PAYMENT_METHOD_SELECTED: 'PaymentMethodSelected',
  ORDER_SUBMITTED: 'OrderSubmitted',
  ORDER_VIEWED: 'OrderViewed',
  ORDER_EDIT_OPEN: 'OrderEditOpen',
  RESEND_COMMUNICATION: 'ResendCommunication',
  ORDER_CANCELED: 'OrderCanceled',
  ORDERS_EXPORTED: 'OrdersExported',
} as const;

export type ResendCommunicationType = 'payment' | 'confirmation' | 'receipt';

export function getDatadogTimestamp() {
  return new Date().toISOString();
}

export function logDatadogEvent(name: string, metadata?: Record<string, unknown>) {
  notifyDatadogRumAction({
    name,
    metadata: {
      ...metadata,
      appVersion: ooeConstants.APP_VERSION,
      timestamp: getDatadogTimestamp(),
    },
  });
  datadogLogs.logger.info(name, {
    ...metadata,
    appVersion: ooeConstants.APP_VERSION,
    timestamp: getDatadogTimestamp(),
  });
}

function toError(error?: Error | SerializedError): Error | undefined {
  if (!error) {
    return undefined;
  }

  if (error instanceof Error) {
    return error;
  }

  const converted = new Error(error.message ?? 'Unknown error');
  converted.name = error.name ?? 'Error';
  if (error.stack) {
    converted.stack = error.stack;
  }
  return converted;
}

export function logDatadogError(
  name: string,
  metadata?: Record<string, unknown>,
  error?: Error | SerializedError,
) {
  datadogLogs.logger.error(
    name,
    {
      ...metadata,
      appVersion: ooeConstants.APP_VERSION,
      timestamp: getDatadogTimestamp(),
    },
    toError(error),
  );
}

export function logDeliveryOrPickupSelectedEvent({ orderId, option }: { orderId?: string; option: string }) {
  logDatadogEvent(DATADOG_EVENT_NAMES.DELIVERY_OR_PICKUP_SELECTED, {
    orderId,
    option,
  });
}

export function logGuestAddedEvent({
  orderId,
  guestId,
  guestDetails,
}: {
  orderId?: string;
  guestId: string;
  guestDetails: Record<string, unknown>;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.GUEST_ADDED, {
    orderId,
    guestId,
    guestDetails,
  });
}

export function logPickupOrDeliveryTimeSetEvent({
  orderId,
  selectedTime,
}: {
  orderId?: string;
  selectedTime: string;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.PICKUP_OR_DELIVERY_TIME_SET, {
    orderId,
    selectedTime,
  });
}

export function logDeliveryAddressAddedEvent({
  orderId,
  addressDetails,
}: {
  orderId?: string;
  addressDetails: Record<string, unknown>;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.DELIVERY_ADDRESS_ADDED, {
    orderId,
    addressDetails,
  });
}

export function logDeliveryAddressValidatedEvent({
  orderId,
  addressDetails,
  validationResponse,
}: {
  orderId?: string;
  addressDetails: Record<string, unknown>;
  validationResponse: Record<string, unknown>;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.DELIVERY_ADDRESS_VALIDATED, {
    orderId,
    addressDetails,
    validationResponse,
  });
}

export function logMenuItemAddedEvent({
  orderId,
  id,
  itemTag,
  dessertTag,
  sideTag,
  premiumSideTag,
}: {
  orderId?: string;
  id: string;
  itemTag: string;
  dessertTag?: string;
  sideTag?: string;
  premiumSideTag?: string;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.MENU_ITEM_ADDED, {
    orderId,
    id,
    itemTag,
    dessertTag,
    sideTag,
    premiumSideTag,
  });
}

export function logPaymentMethodSelectedEvent({
  orderId,
  paymentMethod,
}: {
  orderId?: string;
  paymentMethod: string;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.PAYMENT_METHOD_SELECTED, {
    orderId,
    paymentMethod,
  });
}

export function logOrderSubmittedEvent(metadata: Record<string, unknown>) {
  logDatadogEvent(DATADOG_EVENT_NAMES.ORDER_SUBMITTED, metadata);
}

export function logOrderViewedEvent({ orderId }: { orderId: string }) {
  logDatadogEvent(DATADOG_EVENT_NAMES.ORDER_VIEWED, {
    orderId,
  });
}

export function logOrderEditOpenEvent({ orderId }: { orderId: string }) {
  logDatadogEvent(DATADOG_EVENT_NAMES.ORDER_EDIT_OPEN, {
    orderId,
  });
}

export function logResendCommunicationEvent({
  orderId,
  activeCommunication,
}: {
  orderId: string;
  activeCommunication?: ResendCommunicationType;
}) {
  logDatadogEvent(DATADOG_EVENT_NAMES.RESEND_COMMUNICATION, {
    orderId,
    activeCommunication,
  });
}

export function logOrderCanceledEvent({ orderId }: { orderId: string }) {
  logDatadogEvent(DATADOG_EVENT_NAMES.ORDER_CANCELED, {
    orderId,
  });
}

export function logOrdersExportedEvent({ orderIds }: { orderIds: string[] }) {
  logDatadogEvent(DATADOG_EVENT_NAMES.ORDERS_EXPORTED, {
    orderIds,
  });
}
